import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, DatePicker, Heading, Layout, PageWrap, Table } from '@vwfs-bronson/bronson-react';
import moment from 'moment';
import { PaginationComponent } from '../../../../components/Pagination';
import { getDealerDocumentFile, getDealerDocumentList } from '../../../../services/api/miles/miles-services';
import { useRemoveCardTitle } from '../../../../services/common/useRemoveCardTitle';
import { brokerSelectors } from '../../../../services/redux/features/broker.slice';
import { loadingActions, loadingSelectors } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';

/**
 * Formats a Date object into a string in the format 'DD-MM-YYYY'.
 *
 * @param {Date} date - The date to format.
 * @returns {string} A string representing the formatted date.
 */
function formatDate(date: Date): string {
  return moment(date).format('DD-MM-YYYY');
}

function formatDateToBft(date: string): string {
  const [dd, mm, yyyy] = date.split('-');
  return `${yyyy}-${mm}-${dd}`;
}

const defaultPagination = {
  currentPage: 1,
  pageSize: 20,
  totalCount: 1,
  totalPages: 1,
};

export const DashboardDealerDocuments: FunctionComponent = () => {
  const { t } = useTranslation();
  const globalLoading = useSelector(loadingSelectors.selectLoading);
  useRemoveCardTitle(globalLoading);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { milesCompanyId } = useSelector(brokerSelectors.currentCompany) || {};
  const [documents, setDocuments] = useState<DealerIntegration.Components.Schemas.Document[]>([]);
  const [pagination, setPagination] = useState(defaultPagination);

  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(formatDate(past));
  const [dateTo, setDateTo] = useState(formatDate(today));

  const goBack = () => {
    navigate.dashboardHome();
  };

  const onDatepickerChange = (event) => {
    const [startDate, endDate] = event.split(' au ');

    if (!endDate) {
      return;
    }

    setDateFrom(startDate);
    setDateTo(endDate);
  };

  const onDownload = async (documentId: string, documentName: string) => {
    dispatch(loadingActions.setLoading(true));

    try {
      const result = await getDealerDocumentFile(milesCompanyId, documentId);
      const base64 = result.documents[0]?.file?.base64;

      const fileFormat = documentName.split('.').pop()?.toLowerCase();
      let mimeType: string;

      if (fileFormat === 'xls') {
        mimeType = 'application/vnd.ms-excel';
      } else {
        mimeType = 'application/pdf';
      }

      const blobUrl = `data:${mimeType};base64,${base64}`;

      const hiddenElement = document.createElement('a');
      hiddenElement.href = blobUrl;
      hiddenElement.download = documentName;

      document.body.appendChild(hiddenElement);
      hiddenElement.click();
      document.body.removeChild(hiddenElement);
    } finally {
      dispatch(loadingActions.setLoading(false));
    }
  };

  const getDocuments = async () => {
    if (!milesCompanyId) {
      return;
    }
    dispatch(loadingActions.setLoading(true));
    const { documents, totalCount } = await getDealerDocumentList(
      milesCompanyId || '',
      formatDateToBft(dateFrom),
      formatDateToBft(dateTo),
      pagination.currentPage
    );
    dispatch(loadingActions.setLoading(false));
    setDocuments(documents);
    setPagination((pagination) => ({
      ...pagination,
      totalCount,
      totalPages: Math.ceil(totalCount / defaultPagination.pageSize),
    }));
  };

  const onPaginationUpdate = (item) => {
    setPagination((pagination) => ({
      ...pagination,
      currentPage: item,
    }));
  };

  useEffect(() => {
    getDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage]);

  return (
    <PageWrap>
      <Heading level={1}>{t('dashboard:dealerDocuments:title')}</Heading>

      <Card title="" className="u-mb u-text-left">
        <Layout>
          <Layout.Item default="1/2">
            <Heading level={3}>{t('dashboard:dealerDocuments:startEndDate')}</Heading>
            <DatePicker
              mode="range"
              dateFormat="d-m-Y"
              locale="fr"
              onChange={onDatepickerChange}
              value={dateFrom + ' au ' + dateTo}
            />
          </Layout.Item>
          <Layout.Item>
            <Button disabled={!dateFrom || !dateTo} onClick={getDocuments}>
              {t('dashboard:dealerDocuments:confirm')}
            </Button>
          </Layout.Item>
        </Layout>
      </Card>

      <Card title="" className="u-mb u-text-left">
        <Heading level={3}>{t('dashboard:dealerDocuments:results')}</Heading>
        <Table>
          <Table.Tbody>
            {documents.map((item) => (
              <Table.Tr key={item.documentId}>
                <Table.Td>{item.description ?? item.displayLabel}</Table.Td>
                <Table.Td className="u-text-right">
                  <Button
                    element="a"
                    icon="semantic-download"
                    link
                    secondary
                    simple
                    small
                    ariaLabel="download"
                    onClick={() =>
                      onDownload(item.documentId || '', item.description || item.displayLabel || 'file.pdf')
                    }
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <PaginationComponent pagination={pagination} onChange={onPaginationUpdate} />
      </Card>

      <Layout center className="u-mt">
        <Layout.Item default="4/12">
          <Button full type="button" onClick={goBack}>
            {t('dashboard:dealerDocuments:back')}
          </Button>
        </Layout.Item>
      </Layout>
    </PageWrap>
  );
};
